// Tx Plan Center — shared constants, formatters, state vocabulary.
// Kept in one place so cards, columns, KPI strip, and filter sidebar
// all speak the same visual language.

import {
  FileText, Send, ListChecks, CalendarCheck, Activity, Award, Clock,
  XCircle, PauseCircle, WalletCards, Ban, GitBranch,
} from "lucide-react";

// =====================================================================
// Revenue pipeline states — stored on treatment_plans.status and exposed
// to the UI as snake_case pipeline_state by tx_plan_center_list.
// =====================================================================
// Order here is the kanban-column order, left → right.
export const PIPELINE_STATES = [
  {
    key: "draft",
    label: "Draft",
    Icon: FileText,
    column: "border-slate-300/60 bg-slate-50",
    pill: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
    accent: "text-slate-500",
    sortHint: "Newest first",
  },
  {
    key: "proposed",
    label: "Proposed",
    Icon: Send,
    column: "border-blue-300/60 bg-blue-50/40",
    pill: "bg-blue-100 text-blue-800 ring-1 ring-blue-200",
    accent: "text-blue-700",
    sortHint: "Oldest first — decay risk",
    daysWarn: 14,
    daysAlert: 21,
  },
  {
    // Lifecycle v2: patient said yes but no date locked in. The
    // receptionist's call list — front desk follows up to book
    // Visit 1. Violet to signal "action needed" without alarming.
    key: "needs_booking",
    label: "Needs Booking",
    Icon: ListChecks,
    column: "border-violet-300/60 bg-violet-50/40",
    pill: "bg-violet-100 text-violet-800 ring-1 ring-violet-200",
    accent: "text-violet-700",
    sortHint: "Oldest first — call them",
    daysWarn: 3,
    daysAlert: 7,
  },
  {
    // Lifecycle v2: Visit 1 booked. Plan is locked clinically and
    // logistically; emerald matches the old "Accepted" feel because
    // this is the "all systems go" state.
    key: "scheduled",
    label: "Scheduled",
    Icon: CalendarCheck,
    column: "border-emerald-300/60 bg-emerald-50/40",
    pill: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
    accent: "text-emerald-700",
    sortHint: "Soonest visit first",
    daysWarn: 14,
    daysAlert: 30,
  },
  {
    key: "in_progress",
    label: "In Progress",
    Icon: Activity,
    column: "border-amber-300/60 bg-amber-50/40",
    pill: "bg-amber-100 text-amber-900 ring-1 ring-amber-200",
    accent: "text-amber-700",
    sortHint: "Next visit due",
  },
  {
    key: "paused",
    label: "Paused",
    Icon: PauseCircle,
    column: "border-orange-300/60 bg-orange-50/35",
    pill: "bg-orange-100 text-orange-800 ring-1 ring-orange-200",
    accent: "text-orange-700",
    sortHint: "Oldest hold first",
    daysWarn: 90,
    daysAlert: 180,
  },
  {
    key: "completed",
    label: "Completed",
    Icon: Award,
    column: "border-zinc-300/60 bg-zinc-50",
    pill: "bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200",
    accent: "text-zinc-500",
    sortHint: "Most recent",
  },
  {
    key: "ar",
    label: "AR",
    Icon: WalletCards,
    column: "border-red-300/60 bg-red-50/35",
    pill: "bg-red-100 text-red-800 ring-1 ring-red-200",
    accent: "text-red-700",
    sortHint: "Oldest receivable first",
    daysWarn: 14,
    daysAlert: 30,
  },
  {
    key: "expired",
    label: "Expired",
    Icon: Clock,
    column: "border-rose-300/50 bg-rose-50/30",
    pill: "bg-rose-100 text-rose-800 ring-1 ring-rose-200",
    accent: "text-rose-700",
    sortHint: "Most recent expiry",
  },
  {
    key: "abandoned",
    label: "Abandoned",
    Icon: Ban,
    column: "border-stone-300/60 bg-stone-50/50",
    pill: "bg-stone-100 text-stone-700 ring-1 ring-stone-200",
    accent: "text-stone-600",
    sortHint: "Most recent",
  },
  {
    key: "rejected",
    label: "Rejected",
    Icon: XCircle,
    column: "border-zinc-300/50 bg-zinc-50/60",
    pill: "bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200",
    accent: "text-zinc-600",
    sortHint: "Most recent",
  },
  {
    key: "superseded",
    label: "Superseded",
    Icon: GitBranch,
    column: "border-slate-300/50 bg-slate-50/60",
    pill: "bg-slate-100 text-slate-600 ring-1 ring-slate-200",
    accent: "text-slate-600",
    sortHint: "Most recent",
  },
];

export const STATE_BY_KEY = Object.fromEntries(PIPELINE_STATES.map((s) => [s.key, s]));

// An Ongoing Treatment (MV / is_informal) starts live and is never a proposal,
// so it must only ever sit under In Progress / Paused / Completed / AR — never
// under these proposal-side columns.
const MV_FORBIDDEN_KEYS = new Set(["draft", "proposed", "needs_booking", "scheduled"]);
let mvBucketWarned = false;

// Resolve which kanban column a plan belongs to. Formal plans bucket exactly
// by their pipeline_state (unknown states fall to "abandoned", unchanged
// behavior). Ongoing Treatments (is_informal) get a safety guard: if one ever
// arrives in a proposal-side column (it shouldn't post-Phase-2), it is mapped
// to In Progress instead so an MV never shows in a proposal column.
export function bucketKeyForPlan(plan) {
  const raw = plan?.pipeline_state;
  const key = STATE_BY_KEY[raw] ? raw : "abandoned";
  if (plan?.is_informal && MV_FORBIDDEN_KEYS.has(key)) {
    if (!mvBucketWarned) {
      mvBucketWarned = true;
      // eslint-disable-next-line no-console
      console.warn(
        `[TxPlanCenter] Ongoing treatment ${plan?.plan_code || plan?.id} arrived ` +
        `in proposal column "${key}"; bucketing it under In Progress instead.`
      );
    }
    return "in_progress";
  }
  return key;
}

// =====================================================================
// Currency / numbers
// =====================================================================
// Whole-peso display — kanban cards stay readable.
export function formatMoney(centavos, currency = "PHP") {
  const symbol = currency === "PHP" ? "₱" : currency === "USD" ? "$" : `${currency} `;
  return symbol + Math.round((centavos ?? 0) / 100).toLocaleString("en-US");
}

export function formatPercent(value) {
  if (value === null || value === undefined) return "—";
  return `${Math.round(value * 10) / 10}%`;
}

export function formatDays(value) {
  if (value === null || value === undefined) return "—";
  const n = Math.round(value);
  return n === 1 ? "1 day" : `${n} days`;
}

// =====================================================================
// Days-in-state coloring per-state thresholds
// =====================================================================
export function daysBadgeClass(days, stateKey) {
  const s = STATE_BY_KEY[stateKey];
  if (!s || days == null) return "bg-slate-100 text-slate-600";
  if (s.daysAlert != null && days >= s.daysAlert) return "bg-rose-100 text-rose-700 ring-1 ring-rose-200";
  if (s.daysWarn  != null && days >= s.daysWarn)  return "bg-amber-100 text-amber-800 ring-1 ring-amber-200";
  return "bg-slate-100 text-slate-600";
}

// =====================================================================
// Role → default tab
// =====================================================================
export function defaultTabForRole(clinicRole) {
  if (clinicRole === "receptionist") return "work-queue";
  return "pipeline";
}
